import * as React from 'react';
import { Link, Show, useLocaleState, useRecordContext, useTranslate } from 'react-admin';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import moment from 'moment';
import 'moment/locale/tr';
import {
  formatSaleChannel,
  formatSaleStatus,
  getSaleStatusColor,
  formatPaymentType,
  formatUserFullName,
  formatUserMobile,
  formatEventName,
  formatProductsCount,
  getSaleProducts,
  resolveSalePaymentId,
} from '../utils/salesUtils';
import { getPaymentIdBySale } from '../services/paymentsService';

const FieldRow = ({ label, children }) => {
  if (children == null || children === '' || children === '—') return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
        gap: { xs: 0.25, sm: 2 },
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, pt: { sm: 0.15 } }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
};

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      mb: 2,
    }}
  >
    {title ? (
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, letterSpacing: 0.2 }}>
        {title}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const SaleStatusBadge = ({ record: recordProp }) => {
  const ctx = useRecordContext();
  const record = recordProp || ctx;
  const translate = useTranslate();
  if (!record) return null;
  const label = formatSaleStatus(record.status, translate);
  const color = getSaleStatusColor(record.status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        backgroundColor: color,
        color: '#fff',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {label}
    </Box>
  );
};

/** Davetiye / rezervasyon / 0 TL / ücretsiz etiketi. */
const formatSaleKind = (sale, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (!sale) return t('sales.kind_sale', { _: 'Satış' });
  if (sale.isInvitation) return t('sales.invitation', { _: 'Davetiye' });
  if (sale.isReservation) return t('sales.reservation', { _: 'Rezerve' });
  const total = Number(sale.paymentTotal);
  if (Number.isFinite(total) && total <= 0) {
    return t('sales.kind_free', { _: 'Ücretsiz (0 TL)' });
  }
  if (!sale.paymentType && (sale.paymentTotal == null || sale.paymentTotal === '')) {
    return t('sales.kind_no_payment', { _: 'Ödemesiz satış' });
  }
  return formatPaymentType(sale.paymentType, sale.paymentTotal, translate);
};

const LoadingRedirect = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      py: 8,
    }}
  >
    <CircularProgress size={22} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const FreeSaleDetail = ({ record }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const products = getSaleProducts(record);
  const kind = formatSaleKind(record, translate);
  const amount =
    record.paymentTotal != null && record.paymentTotal !== ''
      ? `${record.paymentTotal} TL`
      : '0 TL';
  const userId = record.user?.id;
  const eventId = record.event?.id;

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', width: '100%', py: 1 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {translate('sales.detail_title', { _: 'Satış Detayı' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {record.id}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              backgroundColor: '#546e7a',
              color: '#fff',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {kind}
          </Box>
          <SaleStatusBadge record={record} />
        </Stack>
      </Stack>

      <Section title={translate('sales.section_summary', { _: 'Özet' })}>
        <FieldRow label={translate('sales.status', { _: 'Durum' })}>
          <SaleStatusBadge record={record} />
        </FieldRow>
        <FieldRow label={translate('sales.kind', { _: 'Tür' })}>{kind}</FieldRow>
        <FieldRow label={translate('sales.payment_amount', { _: 'Tutar' })}>{amount}</FieldRow>
        <FieldRow label={translate('sales.sale_date', { _: 'Satış Tarihi' })}>
          {record.createdate
            ? moment(Number(record.createdate)).locale(locale).format('LL LT')
            : '—'}
        </FieldRow>
        <FieldRow label={translate('sales.order_id', { _: 'Sipariş No' })}>
          {record.oid || '—'}
        </FieldRow>
        <FieldRow label={translate('sales.channel', { _: 'Kanal' })}>
          {formatSaleChannel(record.channel, translate)}
        </FieldRow>
        <FieldRow label={translate('sales.sold_by', { _: 'Satan' })}>
          {record.soldBy || record.soldby || null}
        </FieldRow>
      </Section>

      <Section title={translate('sales.section_user', { _: 'Kullanıcı' })}>
        <FieldRow label={translate('sales.user', { _: 'Ad Soyad' })}>
          {userId ? (
            <Link to={`/users/${userId}`}>{formatUserFullName(record.user)}</Link>
          ) : (
            formatUserFullName(record.user)
          )}
        </FieldRow>
        <FieldRow label={translate('sales.user_email', { _: 'E-posta' })}>
          {record.user?.email || '—'}
        </FieldRow>
        <FieldRow label={translate('sales.user_mobile', { _: 'Telefon' })}>
          {formatUserMobile(record.user) || '—'}
        </FieldRow>
      </Section>

      <Section title={translate('sales.section_event', { _: 'Etkinlik' })}>
        <FieldRow label={translate('sales.event', { _: 'Etkinlik' })}>
          {eventId ? (
            <Link to={`/events-v2/${eventId}/show`}>{formatEventName(record.event)}</Link>
          ) : (
            formatEventName(record.event)
          )}
        </FieldRow>
        <FieldRow label={translate('sales.products_count', { _: 'Ürün Adedi' })}>
          {formatProductsCount(record, translate)}
        </FieldRow>
      </Section>

      {products.length > 0 ? (
        <Section title={translate('sales.section_products', { _: 'Ürünler' })}>
          {products.map((p) => (
            <FieldRow key={`${p.groupKey}-${p.productId}`} label={p.title || p.productId}>
              {`${p.count} adet`}
              {p.price != null ? ` · ${p.price} TL` : ''}
            </FieldRow>
          ))}
        </Section>
      ) : null}

      {(record.seats?.length > 0 || record.tickets?.length > 0) && (
        <Section title={translate('sales.section_tickets', { _: 'Bilet / Koltuk' })}>
          {record.seats?.length > 0 ? (
            <FieldRow label={translate('sales.seats', { _: 'Koltuklar' })}>
              {record.seats
                .map((s) => s.id || s)
                .filter(Boolean)
                .join(', ')}
            </FieldRow>
          ) : null}
          {record.tickets?.length > 0 ? (
            <FieldRow label={translate('sales.tickets', { _: 'Biletler' })}>
              {`${record.tickets.length} bilet`}
            </FieldRow>
          ) : null}
        </Section>
      )}

      {record.coupon?.code ? (
        <Section title={translate('sales.section_coupon', { _: 'Kupon' })}>
          <FieldRow label={translate('sales.coupon', { _: 'Kod' })}>{record.coupon.code}</FieldRow>
        </Section>
      ) : null}
    </Box>
  );
};

const SaleShowBody = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const saleId = record?.id;
  const [phase, setPhase] = React.useState('lookup'); // lookup | redirect | free

  React.useEffect(() => {
    let cancelled = false;
    if (!saleId) {
      setPhase('lookup');
      return undefined;
    }

    const localId = resolveSalePaymentId(record);
    if (localId) {
      setPhase('redirect');
      window.location.replace(`/#/payments/${encodeURIComponent(localId)}/show`);
      return undefined;
    }

    setPhase('lookup');
    getPaymentIdBySale(saleId).then((id) => {
      if (cancelled) return;
      if (id) {
        setPhase('redirect');
        window.location.replace(`/#/payments/${encodeURIComponent(id)}/show`);
      } else {
        setPhase('free');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [saleId, record]);

  if (!record || phase === 'lookup' || phase === 'redirect') {
    return (
      <LoadingRedirect
        message={
          phase === 'redirect'
            ? translate('sales.redirecting_payment', {
                _: 'İade ekranına yönlendiriliyor…',
              })
            : translate('sales.loading_detail', { _: 'Satış yükleniyor…' })
        }
      />
    );
  }

  return <FreeSaleDetail record={record} />;
};

export const SaleShow = () => {
  const translate = useTranslate();

  return (
    <Show
      title={translate('sales.detail_title', { _: 'Satış Detayı' })}
      component="div"
      sx={{
        '& .RaShow-main': { maxWidth: '100%' },
        '& .RaShow-card': {
          boxShadow: 'none',
          background: 'transparent',
        },
      }}
    >
      <SaleShowBody />
    </Show>
  );
};

export default SaleShow;
